import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "styled-components";

import URL_PATTERNS from "../../../urls";
import { DB_ID_SPECIAL_PACK_TYPE, I18N_NAMESPACES } from "../../../helpers/constants";
import { useLayoutContext } from "../Layout/context";
import SpecialPackAddToCartTray from "../SpecialPackAddToCartTray";
import PageMeta from "../../molecules/PageMeta";
import breakpoints from "../../../styles/breakpoints";
import { header, transition } from "../../../styles/variables";
import { SpecialPackEdition_specialPackEdition } from "../../../types/__generated__/SpecialPackEdition";
import SpecialBoxInfo from ".";

const infoBoxFullPageStyleWithHeader = css`
  ${breakpoints.smUp} {
    top: ${header.mobileHeight};
  }
  ${breakpoints.mdUp} {
    top: ${header.desktopHeight};
  }
  top: 0px;
  transition: ${transition.headerMenuBarTransition};
`;

const infoBoxFullPageStyleWithoutHeader = css`
  top: 0px !important;
  transition: ${transition.headerMenuBarTransition};
`;

interface SpecialBoxInfoControllerProps {
  specialPackEdition: SpecialPackEdition_specialPackEdition;
}

const SpecialBoxInfoController = ({ specialPackEdition }: SpecialBoxInfoControllerProps) => {
  const { showHeaderBarFromChild, setShowHeaderBarFromChild } = useLayoutContext();

  const [openAddSpecialPackTray, setOpenAddSpecialPackTray] = useState<boolean>(false);
  const [yValInflectionPoint, setYValInflectionPoint] = useState<number>(0);

  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.SPECIAL_PACKS]);

  const handleScroll = () => {
    const yValue = +window.scrollY;
    const scrollOffSet = 10;
    if (yValue > yValInflectionPoint + scrollOffSet) {
      // scrolling down hides header bar.
      setYValInflectionPoint(yValue);
      setShowHeaderBarFromChild(false);
    } else if (yValue < yValInflectionPoint - scrollOffSet) {
      // scrolling up shows header bar.
      setYValInflectionPoint(yValue);
      setShowHeaderBarFromChild(true);
    }
  };

  useEffect(() => {
    // Show/hide header on scroll
    window.addEventListener("scroll", handleScroll);

    // Cleanup/unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [yValInflectionPoint]);

  const toggleAddSpecialPackTray = () => {
    setShowHeaderBarFromChild(true);
    setOpenAddSpecialPackTray(!openAddSpecialPackTray);
  };

  // Adds a canonical URL to the advent calendar landing page for advent calendar special packs
  // This is to improve SEO ranking for the advent calendar landing page
  const isAdventCalendar = specialPackEdition.specialPackType?.id === DB_ID_SPECIAL_PACK_TYPE.ADVENT_CALENDAR;

  return (
    <>
      <PageMeta
        title={t("common:pageMetaTitle", {
          title: specialPackEdition.seoTitle,
          interpolation: { escapeValue: false },
        })}
        description={specialPackEdition.seoDescription}
        image={specialPackEdition?.heroTopImageLargeUrl || undefined}
        canonicalUrl={isAdventCalendar ? URL_PATTERNS.SPECIAL_PACK_ADVENT_CALENDAR : undefined}
      />
      <SpecialBoxInfo
        // @ts-ignore
        specialPackEdition={specialPackEdition}
        toggleAddSpecialPackTray={toggleAddSpecialPackTray}
        // @ts-ignore
        infoBoxItemProductHolderStyle={
          showHeaderBarFromChild ? infoBoxFullPageStyleWithHeader : infoBoxFullPageStyleWithoutHeader
        }
      />
      {openAddSpecialPackTray && (
        <SpecialPackAddToCartTray
          specialPackEdition={specialPackEdition}
          onClose={toggleAddSpecialPackTray}
          closeTrays={toggleAddSpecialPackTray}
        />
      )}
    </>
  );
};

export default SpecialBoxInfoController;
